/**
 * UpgradeModal - Modal dialog for upgrade prompts
 * Redirects to Stripe Checkout with 7-day free trial
 */

import { useState, useEffect, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/cn';
import { useSubscriptionStore, useAuthStore } from '@/stores';
import { FEATURE_NAMES, SUBSCRIPTION_TIERS, getTierComparison } from '../config';
import type { FeatureKey, SubscriptionTier } from '@/types/subscription';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: FeatureKey;
  targetTier?: SubscriptionTier;
}

export function UpgradeModal({ isOpen, onClose, feature, targetTier = 'pro' }: UpgradeModalProps) {
  const currentTier = useSubscriptionStore((state) => state.tier);
  const getLocalizedPrice = useSubscriptionStore((state) => state.getLocalizedPrice);
  const createCheckoutSession = useSubscriptionStore((state) => state.createCheckoutSession);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tierConfig = SUBSCRIPTION_TIERS[targetTier];
  const price = getLocalizedPrice(tierConfig.priceUSD);
  const comparison = getTierComparison(currentTier, targetTier);

  // Calculate trial end date (7 days from now)
  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + 7);
  const trialEndFormatted = trialEndDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  // Close on escape
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Pre-load auth session so checkout is instant when button is clicked
      const { profile, user } = useAuthStore.getState();
      if (!profile && !user) {
        useAuthStore.getState().refreshSession();
      }
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  const handleStartTrial = async () => {
    setError(null);

    // If not logged in, redirect to signup first
    if (!isAuthenticated) {
      window.location.href = '/signup';
      return;
    }

    setIsProcessing(true);

    try {
      // Get the Stripe price ID for the target tier
      const priceIds: Record<string, string> = {
        pro: import.meta.env.VITE_STRIPE_PRO_PRICE_ID || 'price_pro_monthly',
        enterprise: import.meta.env.VITE_STRIPE_ENTERPRISE_PRICE_ID || 'price_enterprise_monthly',
      };

      const priceId = priceIds[targetTier];
      if (!priceId) {
        setError('Invalid plan selected');
        setIsProcessing(false);
        return;
      }

      // Create Stripe Checkout session — redirects to Stripe
      const checkoutUrl = await createCheckoutSession(priceId);

      if (checkoutUrl) {
        // Redirect to Stripe Checkout page
        window.location.href = checkoutUrl;
      } else {
        setError(useSubscriptionStore.getState().error || 'Could not start checkout. Please try again.');
        setIsProcessing(false);
      }
    } catch {
      setError(useSubscriptionStore.getState().error || 'Something went wrong. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AnimatePresence>
        {isOpen && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              />
            </Dialog.Overlay>

            <Dialog.Content asChild>
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 60 }}
                transition={{ type: 'spring', duration: 0.35, bounce: 0.15 }}
                className={cn(
                  'fixed inset-x-0 bottom-0',
                  'mx-auto max-w-lg',
                  'px-5 pt-5 pb-8',
                  'bg-gradient-to-br from-slate-800 to-slate-900',
                  'border-t border-white/10 rounded-t-3xl',
                  'shadow-2xl shadow-black/60',
                  'z-50'
                )}
              >
                {/* Close button */}
                <Dialog.Close asChild>
                  <button
                    className="absolute top-3 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 text-text-muted hover:text-white hover:bg-white/10 transition-all"
                    aria-label="Close"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </Dialog.Close>

                {/* Drag handle */}
                <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-4" />

                {/* Title + price row */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Dialog.Title className="text-lg font-bold text-white">
                      Start Your Free Trial
                    </Dialog.Title>
                    {feature && (
                      <p className="text-text-secondary text-xs mt-0.5">
                        <span className="text-hud-primary font-medium">{FEATURE_NAMES[feature]}</span> is a Pro feature
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-white">{price.formatted}</span>
                    <span className="text-xs text-text-secondary">/mo</span>
                    <p className="text-[10px] text-text-muted">after trial</p>
                  </div>
                </div>

                {/* Free trial banner with date */}
                <div className="mb-3 px-3 py-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-5 h-5 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-sm font-semibold text-emerald-400">7 days free</p>
                  </div>
                  <p className="text-[11px] text-emerald-400/70 ml-7">
                    You won't be charged until <span className="font-semibold text-emerald-400">{trialEndFormatted}</span>. Cancel anytime.
                  </p>
                </div>

                {/* Features — compact horizontal list */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {comparison.filter(c => c.feature).slice(0, 4).map(({ feature: feat }) => (
                    <span key={feat} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-text-primary">
                      <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feat}
                    </span>
                  ))}
                </div>

                {/* Error message */}
                {error && (
                  <p className="text-red-400 text-xs text-center mb-2">{error}</p>
                )}

                {/* CTA Button — always visible */}
                <button
                  onClick={handleStartTrial}
                  disabled={isProcessing}
                  className={cn(
                    'w-full py-4 rounded-xl font-bold text-center',
                    'bg-gradient-to-r from-hud-primary to-teal-600',
                    'text-white text-base',
                    'hover:shadow-lg hover:shadow-hud-primary/25',
                    'active:scale-[0.98]',
                    'transition-all duration-200',
                    'disabled:opacity-70'
                  )}
                >
                  {isProcessing ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Redirecting to checkout...
                    </span>
                  ) : (
                    'Start 7-Day Free Trial'
                  )}
                </button>

                <div className="flex items-center justify-center gap-1 mt-2">
                  <svg className="w-3 h-3 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-[10px] text-text-muted">Secure payment via Stripe</span>
                </div>

                <button
                  onClick={onClose}
                  className="block w-full py-2 text-center text-sm text-text-muted hover:text-white transition-colors mt-1"
                >
                  Maybe Later
                </button>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}

// Global upgrade modal state management
type ModalHandler = (feature?: FeatureKey, targetTier?: SubscriptionTier, onDismiss?: () => void) => void;
let showUpgradeModalFn: ModalHandler | null = null;

export function setUpgradeModalHandler(handler: ModalHandler | null) {
  showUpgradeModalFn = handler;
}

export function showUpgradeModal(feature?: FeatureKey, targetTier?: SubscriptionTier, onDismiss?: () => void) {
  showUpgradeModalFn?.(feature, targetTier, onDismiss);
}
